import { AddressMonikerResponse } from 'services/address';
import { BusinessDetails } from 'slices/businessDetails';
import { DateFormat, utcDateFormatter } from 'utils/dateFormatter';

import { StreetTypes } from '../../../types/analytics';
import { hashingHandler } from '../helpers';

// Adding capability for GTM update the StreetType constant without going through the SPA deployment cycle.
const StreetTypesList = () =>
  window.qbeTrack?.streetTypeMap ? window.qbeTrack?.streetTypeMap : StreetTypes;
declare type StreetType = keyof typeof StreetTypesList;

/**
 * Normalised Hash Address
 * Confluence: https://qbe-appservices.atlassian.net/wiki/spaces/WA/pages/2379284505/Normalised+Hash+Address
 */
const streetAddressHandler = (
  streetNumberName: string,
  suburb: string,
  state: string,
  postCode: string
) => {
  // Remove unwanted characters and lower case the data points.
  streetNumberName = streetNumberName.toLowerCase();

  // Check Street Number Name contain match Street types
  Object.keys(StreetTypesList).forEach((key) => {
    const regExKey = new RegExp(` ${key}`);
    const streetValue = StreetTypesList[key as StreetType];

    if (regExKey.test(streetNumberName))
      streetNumberName = streetNumberName.replace(key, streetValue);
  });
  const fullStreetAddress = (streetNumberName + suburb + state + postCode)
    .toLowerCase()
    .replace(/,|:|-|_|\(|\)|'|\/|\.|\s/g, '');

  return hashingHandler(fullStreetAddress);
};

/**
 * Get suburb, state, and postCode.
 * @param {object} state The current state of the BusinessDetails - Full Address attribute.
 * @returns {object}
 */
const getBusinessAddress = (state: AddressMonikerResponse) => {
  if (!state) return {};
  const { city, stateCode, postalCode, streetAddress1 } = state;
  const fullRiskAddress = streetAddressHandler(streetAddress1, city, stateCode, postalCode);
  return {
    riskSuburb: city,
    riskState: stateCode,
    riskPostcode: postalCode,
    addressHash: fullRiskAddress,
  };
};

/**
 * Get industry, occupation category and occupation.
 * @param {object} state  The current state of the BusinessDetails reducer.
 * @returns {object}
 */
const getIndustryOccupationHandler = (state: Partial<BusinessDetails>) => {
  if (!state) return {};
  return {
    industry: state.industry?.industryValue,
    occupationCategory: state.occupationCategory?.categoryDescription,
    occupation: state.occupation?.occupation,
  };
};

/**
 * Get Insure Type and Property Type
 */
const getInsurePropertyType = (state: Partial<BusinessDetails>) => {
  if (!state?.insuredType) {
    return {};
  }

  const { insuredType, propertyType } = state;
  if (propertyType) {
    return {
      insuredType: `${insuredType}-${propertyType}`,
    };
  }

  return {
    insuredType,
  };
};

/**
 * BusinessDetail reducer analytics collector function.
 * @param state (mandatory) The current state of the BusinessDetails reducer.
 * @returns
 */
const getBusinessDetails = (state: Partial<BusinessDetails>) => {
  if (!state) return {};
  const policyStart = state.coverStart
    ? utcDateFormatter(state.coverStart, DateFormat.ddMMyyyy)
    : undefined;
  const businessAddress = state.fullBusinessAddress
    ? getBusinessAddress(state.fullBusinessAddress)
    : undefined;
  const industryAndOccupations = getIndustryOccupationHandler(state);
  const insurePropertyType = getInsurePropertyType(state);

  return {
    policyStart,
    ...industryAndOccupations,
    ...businessAddress,
    ...insurePropertyType,
  };
};

export default getBusinessDetails;
